import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Box, 
  Typography, 
  CircularProgress,
  Paper,
  styled,
  Theme,
  Switch,
  Fab,
  Snackbar,
  Alert
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ImageSelectionPopup from './ImageSelectionPopup';

// API base URL
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5003';

// Update the fallback image paths to point to the SVG
const PLACEHOLDER_IMAGE = '/assets/car-placeholder.svg';

// Styled Switch component to match design
const StyledSwitch = styled(Switch)(({ theme }) => ({
  width: 42,
  height: 24,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 1,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(18px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: '#000000',
        opacity: 1,
        border: 0,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: '#000000',
      border: '6px solid #fff',
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 22,
    height: 22,
    backgroundColor: '#fff',
  },
  '& .MuiSwitch-track': {
    borderRadius: 24 / 2,
    backgroundColor: '#000000',
    opacity: 1,
    transition: 'background-color 500ms',
  },
}));

// Styled components
const ListingsContainer = styled(Box)(({ theme }: { theme: Theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
  gap: '20px',
  margin: theme.spacing(2, 0)
}));

const ListingCard = styled(Paper)(({ theme }: { theme: Theme }) => ({
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  borderRadius: '8px',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: theme.shadows[6],
  }
}));

const ListingImage = styled('img')({
  width: '100%',
  height: '160px',
  objectFit: 'cover',
  display: 'block',
  pointerEvents: 'none'
});

const ListingDetails = styled(Box)(({ theme }: { theme: Theme }) => ({
  padding: theme.spacing(2),
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
}));

const ListingTitle = styled(Typography)(({ theme }: { theme: Theme }) => ({
  fontWeight: 600,
  marginBottom: theme.spacing(1),
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  height: '50px',
  pointerEvents: 'none'
}));

const ListingFooter = styled(Box)(({ theme }: { theme: Theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: 'auto',
  paddingTop: theme.spacing(1)
}));

interface Listing {
  id?: string;
  title: string;
  photo?: string;
  price?: string;
  link?: string;
  active: boolean;
  imageIds?: string[];
}

interface UserData {
  username: string;
  xmlLink: string;
}

interface AutobazarListingsProps {
  onListingsChange?: (count: number) => void;
  onActiveListingsChange?: (activeCount: number) => void;
}

// Styled floating save button
const FloatingSaveButton = styled(Fab)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(3),
  right: theme.spacing(3),
  backgroundColor: '#1976d2',
  color: 'white',
  minWidth: '120px',
  height: '56px',
  borderRadius: '28px',
  fontSize: '16px',
  fontWeight: 'bold',
  '&:hover': {
    backgroundColor: '#1565c0',
  },
  '&:disabled': {
    backgroundColor: '#cccccc',
  },
  zIndex: 1000,
}));

export default function AutobazarListings({ onListingsChange, onActiveListingsChange }: AutobazarListingsProps) {
  const [listings, setListings] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedListing, setSelectedListing] = useState<Listing | null>(null);
  const [popupOpen, setPopupOpen] = useState(false);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [changes, setChanges] = useState<Record<string, boolean>>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);

  // Update parent with listings count whenever listings change
  useEffect(() => {
    if (onListingsChange) {
      onListingsChange(listings.length);
    }
    
    // Calculate and update active listings count
    if (onActiveListingsChange) {
      const activeCount = listings.filter(listing => listing.active).length;
      onActiveListingsChange(activeCount);
    }
  }, [listings, onListingsChange, onActiveListingsChange]);

  // Fetch listings from the server that handles XML parsing
  useEffect(() => {
    async function fetchListings() {
      try {
        setLoading(true);
        
        // Get the username from localStorage
        let username = '';
        const storedUser = localStorage.getItem('user');
        
        if (storedUser) {
          try {
            const parsedUser = JSON.parse(storedUser);
            username = parsedUser.username || '';
          } catch (err) {
            console.error('Error parsing stored user:', err);
          }
        }
        
        // Fallback to direct username if necessary
        if (!username) {
          username = localStorage.getItem('username') || '';
        }
        
        // Fallback to a test username if we still don't have one
        if (!username) {
          username = 'mtautos'; // Use a default test username
        }

        // Fetch user data to get XML link
        const userResponse = await axios.get(`${API_URL}/api/user-data`, {
          params: { username }
        });

        if (userResponse.data && userResponse.data.user) {
          setUserData({
            username: username,
            xmlLink: userResponse.data.user.xml_link
          });
        }
        
        // Call our new server endpoint that handles XML parsing
        const response = await axios.get(`${API_URL}/api/autobazar-listings`, {
          params: { username }
        });
        
        if (response.data && response.data.listings) {
          setListings(response.data.listings);
        } else {
          setError('No listings data received from server');
        }
      } catch (err) {
        console.error('Error fetching listings:', err);
        setError('Failed to load listings');
      } finally {
        setLoading(false);
      }
    }

    fetchListings();
  }, []);

  // Handle listing click to open image selection popup
  const handleListingClick = (listing: Listing) => {
    if (!userData) {
      console.error('User data not available');
      return;
    }
    
    setSelectedListing(listing);
    setPopupOpen(true);
  };

  // Close popup
  const handleClosePopup = () => {
    setPopupOpen(false);
    setSelectedListing(null);
  };

  // Handle successful save to refresh listings
  const handleSaveSuccess = async () => {
    try {
      // Get the username from localStorage
      let username = '';
      const storedUser = localStorage.getItem('user');
      
      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          username = parsedUser.username || '';
        } catch (err) {
          console.error('Error parsing stored user:', err);
          username = 'autocentrumbb'; // Default fallback
        }
      } else {
        username = 'autocentrumbb'; // Default fallback
      }
      
      // Refresh listings to get updated imageIds
      const response = await axios.get(`${API_URL}/api/autobazar-listings`, {
        params: { username }
      });
      
      if (response.data && response.data.listings) {
        setListings(response.data.listings);
      }
    } catch (error) {
      console.error('Error refreshing listings after save:', error);
    }
  };

  // Toggle a listing's active state (local only)
  const handleToggleActive = (id: string, event: React.ChangeEvent<HTMLInputElement>) => {
    // Prevent the listing click event from firing
    event.stopPropagation();
    
    // Update local state immediately for UI responsiveness
    setListings(prevListings => 
      prevListings.map(listing => 
        listing.id === id ? { ...listing, active: !listing.active } : listing
      )
    );
    
    // Track this change
    const currentListing = listings.find(listing => listing.id === id);
    if (currentListing) {
      const newActiveState = !currentListing.active;
      setChanges(prevChanges => ({
        ...prevChanges,
        [id]: newActiveState
      }));
      setHasChanges(true);
    }
  };

  // Save all changes to the server
  const handleSaveChanges = async () => {
    if (!hasChanges || saving) return;
    
    setSaving(true);
    setShowErrorMessage(false);
    
    try {
      // Get the username
      let username = '';
      const storedUser = localStorage.getItem('user');
      
      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          username = parsedUser.username || '';
        } catch (err) {
          console.error('Error parsing stored user:', err);
        }
      }
      
      if (!username) {
        username = localStorage.getItem('username') || '';
      }
      
      if (!username) {
        username = 'autocentrumbb'; // Default fallback
      }
      
      // Send all changes to the server
      const updatePromises = Object.entries(changes).map(([listingId, active]) =>
        axios.post(`${API_URL}/api/update-listing-state`, {
          username,
          listingId,
          active
        })
      );
      
      const responses = await Promise.all(updatePromises);
      
      // Check if all updates were successful
      const allSuccessful = responses.every(response => response.data.success);
      
      if (allSuccessful) {
        // Clear changes and show success message
        setChanges({});
        setHasChanges(false);
        setShowSuccessMessage(true);
        console.log(`Successfully saved ${Object.keys(changes).length} listing state changes`);
      } else {
        throw new Error('Some listing updates failed');
      }
      
    } catch (error) {
      console.error('Error saving listing changes:', error);
      setShowErrorMessage(true);
      
      // Revert any failed changes by refetching data
      try {
        const username = 'autocentrumbb'; // Use same logic as above
        const response = await axios.get(`${API_URL}/api/autobazar-listings`, {
          params: { username }
        });
        
        if (response.data && response.data.listings) {
          setListings(response.data.listings);
          setChanges({});
          setHasChanges(false);
        }
      } catch (fetchError) {
        console.error('Error reverting changes:', fetchError);
      }
    } finally {
      setSaving(false);
    }
  };

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {listings.length === 0 ? (
        <Box sx={{ textAlign: 'center', p: 3 }}>
          <Typography>No listings found in your Autobazar feed.</Typography>
        </Box>
      ) : (
        <ListingsContainer>
          {listings.map((listing) => (
            <ListingCard 
              key={listing.id} 
              sx={{ 
                opacity: listing.active ? 1 : 0.6,
                border: changes[listing.id || ''] !== undefined ? '2px solid #1976d2' : 'none',
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}
              onClick={() => handleListingClick(listing)}
            >
              <ListingImage 
                src={listing.photo || "https://via.placeholder.com/150?text=No+Image"}
                alt={listing.title}
                onError={(e) => {
                  console.log(`Image failed to load: ${listing.photo}`);
                  (e.target as HTMLImageElement).src = "https://via.placeholder.com/150?text=Image+Error";
                }}
              />
              <ListingDetails>
                <ListingTitle variant="subtitle1">
                  {listing.title}
                </ListingTitle>
                
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  alignItems: 'center', 
                  marginTop: 'auto',
                  paddingTop: 1
                }}>
                  {listing.price && (
                    <Typography 
                      sx={{ 
                        color: '#e74c3c', 
                        fontWeight: 'bold', 
                        fontSize: '18px',
                        pointerEvents: 'none'
                      }}
                    >
                      {listing.price}
                    </Typography>
                  )}
                  <StyledSwitch 
                    size="small"
                    checked={listing.active}
                    onChange={(event) => handleToggleActive(listing.id || '', event)}
                    onClick={(event) => {
                      console.log('Switch clicked - preventing propagation');
                      event.stopPropagation();
                    }}
                  />
                </Box>
              </ListingDetails>
            </ListingCard>
          ))}
        </ListingsContainer>
      )}
      
      {/* Image Selection Popup */}
      {selectedListing && userData && (
        <ImageSelectionPopup
          isOpen={popupOpen}
          onClose={handleClosePopup}
          onSaveSuccess={handleSaveSuccess}
          listingId={selectedListing.id || ''}
          listingTitle={selectedListing.title}
          xmlLink={userData.xmlLink}
          username={userData.username}
        />
      )}

      {/* Floating Save Button */}
      {hasChanges && (
        <FloatingSaveButton
          variant="extended"
          onClick={handleSaveChanges}
          disabled={saving}
          size="large"
        >
          <SaveIcon sx={{ marginRight: 1 }} />
          Uložiť
        </FloatingSaveButton>
      )}

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccessMessage}
        autoHideDuration={3000}
        onClose={() => setShowSuccessMessage(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={() => setShowSuccessMessage(false)} severity="success">
          Listing states saved successfully!
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={showErrorMessage}
        autoHideDuration={4000}
        onClose={() => setShowErrorMessage(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={() => setShowErrorMessage(false)} severity="error">
          Failed to save listing states. Please try again.
        </Alert>
      </Snackbar>
    </Box>
  );
} 